import './styles.css'

import { ColumnHeightOutlined, DownOutlined, ExportOutlined } from '@ant-design/icons'
import { FlagIcon } from '@components/_SiteWide/Flags/FlagIcon'
import { ProjectedDropToggle } from '@components/_SiteWide/TankDisplay/ProjectedDropToggle'
import { TankFillBar } from '@components/_SiteWide/TankDisplay/TankFillBar'
import { VirtualList } from '@components/_SiteWide/VirtualList'
import { useSiteMonitoringContext } from '@components/StoreMonitoring/SiteMonitoringContext'
import {
  GraviButton,
  Horizontal,
  MaterialIcon,
  MobileControlBar,
  MobileDrawer,
  Texto,
  useDeviceType,
  Vertical,
} from '@gravitate-js/excalibrr'
import { ReadableDifference, TankHasWarning } from '@utils/general'
import { Alert, Collapse } from 'antd'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AlertFilterCards } from '../AlertFilterCards'
import { CustomSearchBar } from '../CustomSearchBar'
import { Filters } from '../Filters'
import { MinNoFitColumn, minRunoutComparator } from '../Grid/columnDefs'
import { RunOutTimeCell } from '../Grid/columnsVars'
import { StoreOrderStatusCell } from '../Grid/StoreOrderStatusCell'
import type { DashboardProps, SiteCollapseHeaderProps, SiteRowProps, TankRowProps } from './types'
import type { TextoProps } from '@gravitate-js/excalibrr/dist/components/DataDisplay/Texto/Texto'

export function Dashboard({ data, rundownToggled, setToggle }: DashboardProps) {
  const navigate = useNavigate()
  const { isLandscape } = useDeviceType()
  const { filters, setFilters, filteredStores, loading, setDetailStoreIds } = useSiteMonitoringContext()

  const tankAlerts = data?.spark_graphs
  const numTankAlerts = useMemo(
    () =>
      data?.spark_graphs
        ?.filter((item) => !item.title.includes('Results'))
        .reduce((acc, val) => acc + val.main_metric, 0),
    [data?.spark_graphs]
  )

  const [activeKey, setActiveKey] = React.useState<string | string[]>([])
  const [tankAlertModalOpen, setTankAlertModalOpen] = useState(false)

  const isAllExpanded = filteredStores?.length && activeKey?.length === filteredStores?.length
  function expandAll() {
    setActiveKey(isAllExpanded ? [] : filteredStores.map((store: Gravitate.Site) => store.store_number))
  }

  function onDetailsClick(store: Gravitate.Site) {
    setDetailStoreIds(stores.map((store: Gravitate.Site) => ({ id: store.id, number: store.store_number })))
    navigate(`/site_monitoring/${store.id}`)
  }

  const stores = useMemo(
    () =>
      [...filteredStores]?.sort((a: Gravitate.Site, b: Gravitate.Site) => minRunoutComparator(a, b, rundownToggled)),
    [filteredStores, rundownToggled]
  )

  return (
    <Vertical height='100%'>
      <div className='px-3 border-bottom'>
        <MobileControlBar
          title='Sites'
          rowCount={filteredStores?.length}
          filters={filters}
          setFilters={setFilters}
          customSearchBar={<CustomSearchBar />}
          customFilterDrawer={<Filters isMobile />}
          customAction={
            <GraviButton
              className='square-ish'
              theme1={isAllExpanded}
              icon={<ColumnHeightOutlined />}
              onClick={expandAll}
            />
          }
          titleAction={
            <div className='pt-2'>
              <ProjectedDropToggle
                isMobile
                value={rundownToggled}
                onChange={() => {
                  setToggle(!rundownToggled)
                }}
              />
            </div>
          }
        />
      </div>
      <Vertical data-test-id='dashboard-container' className='px-3 py-2' style={{ backgroundColor: 'var(--bg-1)' }}>
        {tankAlerts?.length && (
          <Alert
            className='mt-1 mb-2'
            action={
              <GraviButton variant='outlined' size='small' type='text' onClick={() => setTankAlertModalOpen(true)}>
                View
              </GraviButton>
            }
            message={
              <Vertical>
                <Horizontal>
                  <MaterialIcon icon='crisis_alert' size={16} className='pr-1' style={{ paddingTop: '2px' }} />
                  <Vertical>
                    <Texto weight='bold'>Tank alerts</Texto>
                    <Texto>{`${numTankAlerts} tank alerts require attention`}</Texto>
                  </Vertical>
                </Horizontal>
              </Vertical>
            }
            type='info'
            showIcon={false}
          />
        )}
        <VirtualList
          isLoading={loading}
          data={stores}
          itemRenderer={(site: Gravitate.Site) => (
            <SiteRow
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              store={site}
              onDetailsClick={onDetailsClick}
              rundownToggled={rundownToggled}
            />
          )}
        />
      </Vertical>
      <MobileDrawer
        title={`Tank Alerts (${numTankAlerts})`}
        open={tankAlertModalOpen}
        onClose={() => setTankAlertModalOpen(false)}
        height={'auto'}
        extra={
          <GraviButton
            appearance='filled'
            onClick={() => {
              setTankAlertModalOpen(false)
              setFilters({ ...filters, alerts: [] })
            }}
          >
            Clear all
          </GraviButton>
        }
      >
        <AlertFilterCards data={tankAlerts} hideHeader onClose={() => setTankAlertModalOpen(false)} />
      </MobileDrawer>
    </Vertical>
  )
}

export function TankRow({ tank, rundownToggled }: TankRowProps) {
  return (
    <>
      <Horizontal>
        <Texto weight='bold'>Tank {tank.tank_id}</Texto>
        <div className='flex-1'>
          <TankFillBar
            className='px-3'
            fill={tank.percent_filled}
            showWarning={TankHasWarning(tank.run_out_date)}
            lowThreshold={0.5}
          />
        </div>
        <Horizontal>
          <Texto>
            {tank?.current_volume.toFixed(0)}/{tank?.storage_max.toFixed(0)}
          </Texto>
        </Horizontal>
      </Horizontal>
      <Horizontal verticalCenter>
        <Texto style={{ flex: 0.75 }}>{tank.product}</Texto>
        <Horizontal flex={1} gap='3px' verticalCenter>
          <Texto style={{ whiteSpace: 'nowrap' }}>Runout Time:</Texto>
          <RunOutTimeCell row={tank} rundownToggled={Boolean(rundownToggled)} />
        </Horizontal>
        <Horizontal>
          <FlagIcon hideRunout={false} flags={tank.flags} hideOrderNumber />
        </Horizontal>
      </Horizontal>
    </>
  )
}

function SiteCollapseHeader({ activeKey, store, onDetailsClick, rundownToggled }: SiteCollapseHeaderProps) {
  const isExpanded = useMemo(() => activeKey.includes(store.store_number), [activeKey, store.store_number])
  const flag = store.flags[0]
  return (
    <Vertical gap='2px'>
      <Horizontal data-test-id='site-panel-header' verticalCenter gap='2px' justifyContent='space-between'>
        <Horizontal verticalCenter>
          <GraviButton appearance='filled' className='expand-icon-container'>
            <DownOutlined className={`expand-icon ${isExpanded ? 'rotated' : ''}`} />
          </GraviButton>
          <Texto className='store-name text-ellipsis' category='h5' weight='bold'>
            {store.name}
          </Texto>
        </Horizontal>
        <Horizontal verticalCenter gap='4px'>
          {store.flags.length === 1 && (
            <Texto
              className='flag-name-container'
              appearance={flag?.severity as TextoProps['appearance']}
              align='right'
            >
              {flag?.name}
            </Texto>
          )}
          <div onClick={(e) => e.stopPropagation()} onKeyPress={(e) => e.stopPropagation()} role='button' tabIndex={0}>
            <FlagIcon hideRunout={false} flags={store.flags} hideOrderNumber />
          </div>
        </Horizontal>
      </Horizontal>
      <Horizontal
        data-test-id='site -second-row'
        verticalCenter
        style={{ flexWrap: 'wrap', width: '100%', alignItems: 'start' }}
      >
        <Texto>#{store.store_number}</Texto>
        <Vertical>
          <Horizontal verticalCenter justifyContent='flex-end'>
            <Texto appearance='medium' className='pr-1'>
              Min Runout:
            </Texto>
            <Texto>
              {ReadableDifference(
                rundownToggled ? store.soonest_run_out_with_drops : store.soonest_run_out,
                undefined,
                true,
                false,
                false
              )}
            </Texto>
          </Horizontal>
          {store.retain && (
            <Horizontal verticalCenter justifyContent='flex-end'>
              <Texto appearance='medium' className='pr-1'>
                Min No Fit:
              </Texto>
              <div onClick={(e) => e.stopPropagation()}>
                <MinNoFitColumn data={store} value={store.retain} size='tiny' />
              </div>
            </Horizontal>
          )}
        </Vertical>
      </Horizontal>
      <Horizontal verticalCenter justifyContent='space-between'>
        <div onClick={(e) => e.stopPropagation()}>
          <StoreOrderStatusCell status={store.order_status} storeId={store.id} isCompact />
        </div>
        <GraviButton
          size='small'
          icon={<ExportOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            onDetailsClick(store)
          }}
        >
          Details
        </GraviButton>
      </Horizontal>
    </Vertical>
  )
}

function SiteRow({ activeKey, setActiveKey, store, onDetailsClick, rundownToggled }: SiteRowProps) {
  if (!store) return null
  return (
    <Collapse
      activeKey={activeKey}
      onChange={(key) => {
        setActiveKey(key)
      }}
      className='p-2 my-2'
      expandIcon={undefined}
      style={{ background: 'none' }}
    >
      <Collapse.Panel
        className='mobile-collapse-panel'
        header={
          <SiteCollapseHeader
            activeKey={activeKey}
            store={store}
            onDetailsClick={onDetailsClick}
            rundownToggled={rundownToggled}
          />
        }
        key={store.store_number}
        showArrow={false}
      >
        {store?.tanks?.map((tank: Gravitate.Tank) => {
          return (
            <Vertical key={tank.tank_id} className='p-2 pl-3' gap='4px'>
              <TankRow tank={tank} rundownToggled={rundownToggled} />
            </Vertical>
          )
        })}
      </Collapse.Panel>
    </Collapse>
  )
}
